import { useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  ChevronDown,
  Database,
  FileCode,
  FileText,
  Globe2,
  KeyRound,
  Layers,
  ListChecks,
  Lock,
  Mail,
  PlugZap,
  Settings,
  Shield,
  Target,
  Upload,
} from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Sub-Navigation für /settings (UX-Carry-Over Sprint 4).
 *
 * Bestehende Routes bleiben unter /settings/<name> (z.B. /settings/import).
 * Neu hinzu kommen /settings/version-targets (Sprint 4) und Stubs für Sprint 5.
 *
 * Struktur:
 *   - Gruppen mit collapsible Sections (Default offen)
 *   - Active-State per pathname-Match
 *   - Mobile-friendly: Default ist Sticky-Sidebar links; auf <md> zeigen wir
 *     die SubNav als Top-Bar mit horizontalem Scrolling.
 *
 * Komponente ist bewusst datendriven, damit der /settings-Index die gleiche
 * Quelle nutzen kann (Card-Auflistung).
 */

export interface SubNavItem {
  to: string;
  label: string;
  icon: ReactNode;
  description?: string;
  /** Sprint-Stub-Marker (rendered als kleines Badge "Sprint 5"). */
  sprintStub?: string;
  /** Wenn true, ist der Link nicht klickbar (Stub für kommenden Sprint). */
  disabled?: boolean;
}

export interface SubNavGroup {
  id: string;
  label: string;
  items: SubNavItem[];
}

export const SETTINGS_GROUPS: SubNavGroup[] = [
  {
    id: 'data',
    label: 'Daten & Import',
    items: [
      {
        to: '/settings/import',
        label: 'Inventar (.ods)',
        icon: <Upload className="size-4" />,
        description: 'Bulk-Import der Wartungstabelle',
      },
      {
        to: '/settings/service-types',
        label: 'Service-Typen',
        icon: <Layers className="size-4" />,
        description: 'Stack-Lookup + Mapping',
      },
      {
        to: '/settings/contracts/mapping',
        label: 'Vertrags-Mapping',
        icon: <FileText className="size-4" />,
        description: 'Invoice-Ninja-Vertrag <-> Kunde',
      },
    ],
  },
  {
    id: 'integrations',
    label: 'Integrationen',
    items: [
      {
        to: '/settings/integrations',
        label: 'Invoice Ninja & HNF',
        icon: <PlugZap className="size-4" />,
        description: 'Sync-Status + Mapping-Coverage',
      },
      {
        to: '/settings/mail',
        label: 'Mail (SMTP)',
        icon: <Mail className="size-4" />,
        description: 'SMTP-Status, Testmail, Vorlagen',
      },
    ],
  },
  {
    id: 'security',
    label: 'Sicherheit',
    items: [
      {
        to: '/settings/secrets',
        label: 'PVE-API-Tokens',
        icon: <KeyRound className="size-4" />,
        description: 'Argon2-encrypted',
      },
      {
        to: '/settings/ssh-config',
        label: 'SSH-Konfig',
        icon: <FileCode className="size-4" />,
        description: '~/.ssh/config.d/wartungstool',
      },
      {
        to: '/settings/secrets',
        label: 'Master-Passwort',
        icon: <Lock className="size-4" />,
        description: 'Rotieren des KEK',
        sprintStub: 'Geplant',
        disabled: true,
      },
      {
        to: '/settings/audit',
        label: 'Audit-Log',
        icon: <Shield className="size-4" />,
        description: 'Append-only Trail + Hash-Chain-Verify',
      },
    ],
  },
  {
    id: 'maintenance',
    label: 'Wartung',
    items: [
      {
        to: '/settings/version-targets',
        label: 'Soll-Versionen',
        icon: <Target className="size-4" />,
        description: 'Pro Service-Type pflegen',
      },
      {
        to: '/routines',
        label: 'Routinen-Library',
        icon: <ListChecks className="size-4" />,
        description: 'Playbook-Templates',
      },
      {
        to: '/settings',
        label: 'Status-Page',
        icon: <Globe2 className="size-4" />,
        description: 'Public Status-Seite',
        sprintStub: 'Zurückgestellt',
        disabled: true,
      },
      {
        to: '/settings/mail',
        label: 'Mail-Templates',
        icon: <Database className="size-4" />,
        description: 'Vorlagen für Kundenmails',
      },
    ],
  },
];

interface SettingsSubNavProps {
  className?: string;
}

export function SettingsSubNav({ className }: SettingsSubNavProps): JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <nav
      aria-label="Einstellungs-Navigation"
      className={['flex flex-col gap-2 text-sm', className ?? ''].join(' ')}
      data-testid="settings-sub-nav"
    >
      <header className="flex items-center gap-2 px-2 py-1 text-text">
        <Settings className="size-4 text-muted" aria-hidden="true" />
        <span className="font-medium">Einstellungen</span>
      </header>
      <ul className="flex flex-col gap-3">
        {SETTINGS_GROUPS.map((group) => (
          <SubNavGroupSection
            key={group.id}
            group={group}
            currentPath={pathname}
          />
        ))}
      </ul>
    </nav>
  );
}

interface SubNavGroupSectionProps {
  group: SubNavGroup;
  currentPath: string;
}

function SubNavGroupSection({
  group,
  currentPath,
}: SubNavGroupSectionProps): JSX.Element {
  const [open, setOpen] = useState(true);
  const hasActive = group.items.some(
    (i) => !i.disabled && isItemActive(currentPath, i.to)
  );
  const sectionId = `subnav-section-${group.id}`;
  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={sectionId}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded-md px-2 py-1 text-xs font-semibold uppercase tracking-wider text-muted hover:text-text"
      >
        <span>{group.label}</span>
        <ChevronDown
          className={['size-3 transition-transform', open ? '' : '-rotate-90'].join(' ')}
          aria-hidden="true"
        />
      </button>
      {open && (
        <ul id={sectionId} className="mt-1 flex flex-col gap-0.5">
          {group.items.map((item, idx) => (
            <li key={`${group.id}-${item.to}-${item.label}-${idx}`}>
              <SubNavItemLink item={item} active={isItemActive(currentPath, item.to)} hint={hasActive} />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

interface SubNavItemLinkProps {
  item: SubNavItem;
  active: boolean;
  /** True wenn ein anderer Eintrag in der Gruppe aktiv ist (visueller Hint). */
  hint: boolean;
}

function SubNavItemLink({ item, active }: SubNavItemLinkProps): JSX.Element {
  const baseClass =
    'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors';
  if (item.disabled) {
    return (
      <span
        className={[baseClass, 'cursor-not-allowed text-muted'].join(' ')}
        title={item.sprintStub ?? 'Bald verfügbar'}
        aria-disabled="true"
      >
        {item.icon}
        <span className="flex-1 truncate">{item.label}</span>
        {item.sprintStub && (
          <span className="rounded-md border border-border bg-surface-2 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted">
            {item.sprintStub}
          </span>
        )}
      </span>
    );
  }
  return (
    <Link
      to={item.to}
      className={[
        baseClass,
        active
          ? 'bg-primary/10 font-medium text-primary'
          : 'text-text hover:bg-surface-2',
      ].join(' ')}
      data-active={active ? 'true' : undefined}
      aria-current={active ? 'page' : undefined}
    >
      {item.icon}
      <span className="flex-1 truncate">{item.label}</span>
    </Link>
  );
}

/**
 * Active-Match-Logik: exact match auf den Pfad oder Präfix-Match auf
 * /settings/<route>/* (z.B. /settings/contracts/mapping ist active wenn
 * man unter /settings/contracts/mapping/details ist).
 *
 * Spezialfall: /settings (ohne Suffix) zählt nur als active für den Index.
 */
export function isItemActive(currentPath: string, to: string): boolean {
  // Trailing slashes normalisieren.
  const cur = currentPath.replace(/\/$/, '');
  const target = to.replace(/\/$/, '');
  if (cur === target) return true;
  // Präfix-Match nur, wenn target tiefer als /settings ist.
  if (target.startsWith('/settings/') && cur.startsWith(`${target}/`)) return true;
  return false;
}
